import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subtitleEntryId } from "../../utils/subtitleEntryId";
import MainVideoUpload from "./MainVideoUpload";
import TrailerUpload from "./TrailerUpload";
import HeroImageUpload from "./HeroImageUpload";
import ThumbnailUpload from "./ThumbnailUpload";
import SubtitlesUpload from "./SubtitlesUpload";
import {
  ServerProbeState,
  TranscodeStatusView,
  VrMediaVerification,
} from "./VrMediaVerification";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import { assetApi } from "shared/api/client/orval";
import {
  uploadVideoToR2,
  R2MultipartUploadAbortedError,
} from "../../api/r2MultipartUpload";
import { postVideoProbe } from "../../api/upload.api";
import { useTranscodeStatus } from "../../hooks/useTranscodeStatus";
import {
  ProbeVideoRole,
  VideoProbeResult,
  inspectVideoFile,
  validateVideoMetadata,
} from "../../utils/videoProbe";

type UploadStage = "idle" | "uploading" | "uploaded" | "error";
type VideoField = "mainVideo" | "trailer";

export type UploadStages = Record<
  Exclude<keyof FileFields, "subtitles">,
  UploadStage
> & {
  subtitles: UploadStage[];
};

const VIDEO_ROLE: Record<VideoField, ProbeVideoRole> = {
  mainVideo: "MainVideo",
  trailer: "Trailer",
};

const VIDEO_LABEL: Record<VideoField, string> = {
  mainVideo: "Main video",
  trailer: "Trailer",
};

/** Re-evaluates a stored probe against the currently selected VR format. */
function revalidateProbe(
  result: VideoProbeResult | undefined,
  format: string | undefined,
  field: VideoField
): VideoProbeResult | null {
  if (!result) return null;
  if (!result.probed) return result;
  return {
    ...result,
    validation: validateVideoMetadata(
      result.metadata,
      format,
      VIDEO_ROLE[field]
    ),
  };
}

type FileFields = {
  mainVideo: File | null;
  trailer: File | null;
  heroImage: File | null;
  thumbnail: File | null;
  subtitles: File[];
};

interface UploadFileProps {
  files: FileFields;
  onFileChange: (
    field: keyof FileFields
  ) => (file: File | File[] | null) => void;
  onAssetAndFileIdsUpdate: (ids: {
    assetId?: string;
    videoIds?: string[];
    imageIds?: string[];
    subtitleIds?: string[];
  }) => void;
  assetId: string;
  mainVideoId?: string;
  trailerId?: string;
  heroImageId?: string;
  thumbnailId?: string;
  videoIds?: string[];
  subtitleIds?: string[];
  isMainVideoInvalid?: boolean;
  isHeroImageInvalid?: boolean;
  isEditingPublished?: boolean;
  /** Selected VR format ('2d' | '180' | '180-stereo' | '360' | '360-stereo'). */
  vrFormat?: string;
  /** Reports the current per-field upload stages so the page can gate submit. */
  onUploadStagesChange?: (stages: UploadStages) => void;
}

const uploadEndpoints: Record<keyof FileFields, string> = {
  mainVideo: "/api/Asset/upload-main-video",
  trailer: "/api/Asset/upload-trailer",
  heroImage: "/api/Asset/upload-hero-image",
  thumbnail: "/api/Asset/upload-thumbnail",
  subtitles: "/api/Asset/upload-subtitle",
};

const UploadFile: React.FC<UploadFileProps> = ({
  files,
  onFileChange,
  onAssetAndFileIdsUpdate,
  assetId,
  mainVideoId,
  trailerId,
  heroImageId,
  thumbnailId,
  videoIds,
  subtitleIds,
  isMainVideoInvalid = false,
  isHeroImageInvalid = false,
  isEditingPublished = false,
  vrFormat,
  onUploadStagesChange,
}) => {
  const [fileUploadStages, setUploadStages] = useState<UploadStages>({
    mainVideo: "idle",
    trailer: "idle",
    heroImage: "idle",
    thumbnail: "idle",
    subtitles: [],
  });

  const [progresses, setProgresses] = useState<
    Record<Exclude<keyof FileFields, "subtitles">, number> & {
      subtitles: number[];
    }
  >({
    mainVideo: 0,
    trailer: 0,
    heroImage: 0,
    thumbnail: 0,
    subtitles: [],
  });

  const [probeResults, setProbeResults] = useState<
    Partial<Record<VideoField, VideoProbeResult>>
  >({});
  const [probing, setProbing] = useState<Partial<Record<VideoField, boolean>>>(
    {}
  );
  const [serverProbe, setServerProbe] = useState<
    Partial<Record<VideoField, ServerProbeState>>
  >({});
  const [transcodeVideoIds, setTranscodeVideoIds] = useState<
    Partial<Record<VideoField, string>>
  >({});
  const mainTranscode = useTranscodeStatus(transcodeVideoIds.mainVideo);
  const trailerTranscode = useTranscodeStatus(transcodeVideoIds.trailer);

  const vrFormatRef = useRef(vrFormat);
  vrFormatRef.current = vrFormat;

  const resetVideoMeta = useCallback(
    (field: VideoField, options: { keepProbe?: boolean } = {}) => {
      if (!options.keepProbe) {
        setProbeResults((prev) => ({ ...prev, [field]: undefined }));
      }
      setProbing((prev) => ({ ...prev, [field]: false }));
      setServerProbe((prev) => ({ ...prev, [field]: "idle" }));
      setTranscodeVideoIds((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const [subtitleEntries, setSubtitleEntries] = useState<Record<string, {
    stage: UploadStage;
    progress: number;
    id?: string;
    language?: string;
  }>>({});
  const uploadStages = useMemo(() => ({
    ...fileUploadStages,
    subtitles: files.subtitles.map((file) => subtitleEntries[subtitleEntryId(file)]?.stage || ((file as any).isPlaceholder ? "uploaded" : "idle")),
  }), [fileUploadStages, files.subtitles, subtitleEntries]);
  const currentSubtitles = useRef(files.subtitles);
  currentSubtitles.current = files.subtitles;
  const subtitleXhrs = useRef(new Map<string, XMLHttpRequest>());
  const mounted = useRef(true);
  useEffect(() => {
    onUploadStagesChange?.(uploadStages);
  }, [uploadStages, onUploadStagesChange]);

  const [assetIds, setAssetIds] = useState<
    Partial<Record<Exclude<keyof FileFields, "subtitles">, string>> & {
      subtitles?: string[];
    }
  >({});
  const [imageIds, setImageIds] = useState<
    Partial<Record<"heroImage" | "thumbnail", string>>
  >({});

  useEffect(() => {
    onAssetAndFileIdsUpdate({ imageIds: Object.values(imageIds).filter((id): id is string => Boolean(id)) });
  }, [imageIds, onAssetAndFileIdsUpdate]);

  useEffect(() => {
    onAssetAndFileIdsUpdate({
      videoIds: [assetIds.mainVideo, assetIds.trailer].filter((id): id is string => Boolean(id)),
    });
  }, [assetIds.mainVideo, assetIds.trailer, assetIds.subtitles, onAssetAndFileIdsUpdate]);

  const completeSubtitleIds = useMemo(() => files.subtitles.map((file) =>
    subtitleEntries[subtitleEntryId(file)]?.id || (file as any).subtitleId
  ).filter(Boolean) as string[], [files.subtitles, subtitleEntries]);
  useEffect(() => {
    onAssetAndFileIdsUpdate({ subtitleIds: completeSubtitleIds });
  }, [completeSubtitleIds, onAssetAndFileIdsUpdate]);

  const getSubtitleId = useCallback(
    (index: number): string | undefined => {
      const file = currentSubtitles.current[index];
      return file ? subtitleEntries[subtitleEntryId(file)]?.id || (file as any).subtitleId : undefined;
    },
    [subtitleEntries]
  );

  const xhrRefs = useRef<
    Partial<Record<keyof FileFields, XMLHttpRequest | XMLHttpRequest[]>>
  >({});
  const previousFiles = useRef<Partial<FileFields>>({});

  const abortControllersRef = useRef<
    Partial<Record<"mainVideo" | "trailer", AbortController>>
  >({});

  // Synchronous mirror of the video IDs so that concurrent mainVideo/trailer
  // completions each see the other's latest ID instead of a stale closure.
  const videoIdsRef = useRef<Partial<Record<"mainVideo" | "trailer", string>>>(
    {}
  );

  useEffect(() => {
    videoIdsRef.current = {
      mainVideo: assetIds.mainVideo,
      trailer: assetIds.trailer,
    };
  }, [assetIds.mainVideo, assetIds.trailer]);

  // Publish every known video ID (mainVideo + trailer) so neither overwrites the other.
  const publishVideoIds = useCallback(
    (field: "mainVideo" | "trailer", id: string) => {
      videoIdsRef.current = { ...videoIdsRef.current, [field]: id };
      setAssetIds((prev) => ({ ...prev, [field]: id }));
      const { mainVideo, trailer } = videoIdsRef.current;
      onAssetAndFileIdsUpdate({
        videoIds: [mainVideo, trailer].filter(
          (value): value is string => Boolean(value)
        ),
      });
    },
    [onAssetAndFileIdsUpdate]
  );

  // Abort any in-flight multipart uploads when the component unmounts.
  useEffect(() => {
    const controllers = abortControllersRef.current;
    const xhrs = xhrRefs.current;
    const subtitles = subtitleXhrs.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(xhrs).forEach((value) => {
        (Array.isArray(value) ? value : [value]).forEach((xhr) => xhr?.abort());
      });
      subtitles.forEach((xhr) => xhr.abort());
      subtitles.clear();
      (Object.keys(controllers) as Array<"mainVideo" | "trailer">).forEach(
        (field) => {
          controllers[field]?.abort();
          controllers[field] = undefined;
        }
      );
    };
  }, []);

  const handleUploadError = useCallback(
    (field: keyof FileFields, error: unknown) => {
      console.error(`Upload failed for ${field}:`, error);
      if (field === "subtitles") {
        // For subtitles, we don't reset the entire array on error
        return;
      }

      if (field === "mainVideo" || field === "trailer") {
        resetVideoMeta(field);
      }

      // Reset all states related to this field
      setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
      setProgresses((prev) => ({ ...prev, [field]: 0 }));

      // Clear the file reference
      onFileChange(field)(null);

      // Clear any XHR references
      if (xhrRefs.current[field]) {
        xhrRefs.current[field] = undefined;
      }

      // Clear any IDs associated with this field
      if (field === "heroImage" || field === "thumbnail") {
        setImageIds((prev) => ({ ...prev, [field]: undefined }));
      } else {
        setAssetIds((prev) => ({ ...prev, [field]: undefined }));
      }

      const message = error instanceof Error ? error.message : "Upload failed";
      alert(message);
    },
    [onFileChange, resetVideoMeta]
  );

  const handleHttpError = useCallback((xhr: XMLHttpRequest) => {
    let errorMessage = `Upload failed: ${xhr.status}`;
    try {
      const errorResponse = JSON.parse(xhr.responseText);

      // Check for different possible error message formats
      if (errorResponse.error) {
        errorMessage = errorResponse.error;
      } else if (xhr.status === 400 && errorResponse.errors) {
        errorMessage = `Validation error: ${JSON.stringify(errorResponse.errors)}`;
      } else if (errorResponse.title) {
        errorMessage = errorResponse.title;
      } else if (errorResponse.message) {
        errorMessage = errorResponse.message;
      } else {
        errorMessage = xhr.responseText || "Unknown error occurred";
      }
    } catch {
      errorMessage = xhr.responseText || "No additional info";
    }
    return new Error(errorMessage);
  }, []);

  const uploadFile = useCallback(
    async (field: keyof FileFields, file: File | File[]) => {
      // Skip uploading placeholder files
      if ((file as any)?.isPlaceholder) {
        return;
      }
      const token = getCookie("userToken");
      if (!token) {
        alert("Please log in to upload content");
        window.location.href = "/login";
        return;
      }
      if (!assetId) {
        alert("Asset ID is not available. Please wait for asset creation.");
        return;
      }
      if (field === "subtitles" && Array.isArray(file)) {
        return;
      }
      if (Array.isArray(file)) {
        throw new Error(`Unexpected array for field ${field}`);
      }

      if (field === "mainVideo" || field === "trailer") {
        setUploadStages((prev) => ({ ...prev, [field]: "uploading" }));
        setProgresses((prev) => ({ ...prev, [field]: 0 }));

        if (abortControllersRef.current[field]) {
          abortControllersRef.current[field]?.abort();
        }
        const controller = new AbortController();
        abortControllersRef.current[field] = controller;
        // A newer upload for this field (or cancel/unmount) replaces the
        // controller; anything still running for the old one must go silent.
        const isCurrent = () => abortControllersRef.current[field] === controller;

        try {
          setProbing((prev) => ({ ...prev, [field]: true }));
          setServerProbe((prev) => ({ ...prev, [field]: "idle" }));
          setTranscodeVideoIds((prev) => ({ ...prev, [field]: undefined }));
          const probe = await inspectVideoFile(
            file,
            vrFormatRef.current,
            VIDEO_ROLE[field]
          );
          if (!isCurrent()) return;
          setProbing((prev) => ({ ...prev, [field]: false }));
          setProbeResults((prev) => ({ ...prev, [field]: probe }));

          if (probe.validation.errors.length > 0) {
            setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
            setProgresses((prev) => ({ ...prev, [field]: 0 }));
            onFileChange(field)(null);
            alert(
              `${VIDEO_LABEL[field]} cannot be uploaded:\n- ${probe.validation.errors.join("\n- ")}`
            );
            return;
          }

          const result = await uploadVideoToR2(file, {
            assetUploadId: assetId,
            role: field,
            signal: controller.signal,
            onProgress: (percent) => {
              if (!isCurrent()) return;
              setProgresses((prev) => ({ ...prev, [field]: percent }));
            },
          });

          if (!isCurrent()) return;

          setProgresses((prev) => ({ ...prev, [field]: 100 }));
          setTranscodeVideoIds((prev) => ({
            ...prev,
            [field]: result.videoId.toString(),
          }));

          const ok = await postVideoProbe(result.videoId);
          if (!isCurrent()) return;
          publishVideoIds(field, result.videoId.toString());
          setServerProbe((prev) => ({ ...prev, [field]: ok ? "sent" : "skipped" }));
          setUploadStages((prev) => ({ ...prev, [field]: "uploaded" }));
          return;
        } catch (err) {
          if (!isCurrent()) return;
          setProbing((prev) => ({ ...prev, [field]: false }));
          if (err instanceof R2MultipartUploadAbortedError) {
            setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
            setProgresses((prev) => ({ ...prev, [field]: 0 }));
            return;
          }
          handleUploadError(field, err);
          throw err;
        } finally {
          if (isCurrent()) {
            abortControllersRef.current[field] = undefined;
          }
        }
      }

      setUploadStages((prev) => ({ ...prev, [field]: "uploading" }));
      setProgresses((prev) => ({ ...prev, [field]: 0 }));
      if (xhrRefs.current[field]) {
        (xhrRefs.current[field] as XMLHttpRequest).abort();
      }
      try {
        return new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhrRefs.current[field] = xhr;
          const isCurrent = () => mounted.current && xhrRefs.current[field] === xhr;
          const formData = new FormData();
          formData.append("file", file);
          formData.append("assetUploadId", assetId);
          xhr.open("POST", `${getApiOrigin()}${uploadEndpoints[field]}`);
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          let lastProgress = 0;
          xhr.upload.onprogress = (event) => {
            if (!isCurrent()) return;
            if (event.lengthComputable) {
              const roundedProgress = Math.floor(
                (event.loaded / event.total) * 100
              );
              if (roundedProgress !== lastProgress) {
                setProgresses((prev) => ({
                  ...prev,
                  [field]: roundedProgress,
                }));
                lastProgress = roundedProgress;
              }
            }
          };
          xhr.onload = () => {
            if (!isCurrent()) { resolve(); return; }
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const responseJson = JSON.parse(xhr.responseText);
                if (field === "heroImage" || field === "thumbnail") {
                  const imageId = responseJson.imageId.toString();
                  setImageIds((prev) => ({
                    ...prev,
                    [field]: imageId,
                  }));
                } else {
                  const id = (
                    responseJson.videoId ||
                    responseJson.subtitleId ||
                    responseJson.filePath
                  ).toString();
                  if (id) {
                    if ((field as string) === "mainVideo" || (field as string) === "trailer") {
                      publishVideoIds(field as "mainVideo" | "trailer", id);
                    } else {
                      setAssetIds((prev) => ({
                        ...prev,
                        [field]: id,
                      }));
                    }
                  }
                }
                setProgresses((prev) => ({ ...prev, [field]: 100 }));
                setUploadStages((prev) => ({ ...prev, [field]: "uploaded" }));
                resolve();
              } catch (err) {
                handleUploadError(field, err);
                reject(err);
              }
            } else {
              const error = handleHttpError(xhr);
              handleUploadError(field, error);
              reject(error);
            }
          };
          xhr.onerror = () => {
            if (!isCurrent()) { resolve(); return; }
            const error = new Error("Network error");
            handleUploadError(field, error);
            reject(error);
          };
          xhr.onabort = () => {
            resolve();
          };
          xhr.onloadend = () => {
            if (xhrRefs.current[field] === xhr) xhrRefs.current[field] = undefined;
          };
          xhr.send(formData);
        });
      } catch (err) {
        handleUploadError(field, err);
        throw err;
      }
    },
    [assetId, onFileChange, publishVideoIds, handleUploadError, handleHttpError]
  );

  const uploadSubtitle = useCallback(
    async (file: File) => {
      // Skip uploading placeholder subtitle files
      if ((file as any)?.isPlaceholder) {
        return;
      }
      const token = getCookie("userToken");
      if (!token) {
        alert("Please log in to upload subtitles");
        window.location.href = "/login";
        return;
      }
      if (!assetId) {
        alert("Asset ID is not available. Please wait for asset creation.");
        return;
      }
      const entryId = subtitleEntryId(file);
      if (subtitleXhrs.current.has(entryId)) return;
      setSubtitleEntries((prev) => ({ ...prev, [entryId]: { stage: "uploading", progress: 0 } }));
      try {
        return new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          subtitleXhrs.current.set(entryId, xhr);
          const isCurrent = () => mounted.current && subtitleXhrs.current.get(entryId) === xhr && currentSubtitles.current.some((entry) => subtitleEntryId(entry) === entryId);
          const formData = new FormData();
          formData.append("file", file);
          formData.append("assetUploadId", assetId);
          xhr.open("POST", `${getApiOrigin()}${uploadEndpoints.subtitles}`);
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.upload.onprogress = (event) => {
            if (!isCurrent()) return;
            if (event.lengthComputable) {
              const progress = Math.floor((event.loaded / event.total) * 100);
              setSubtitleEntries((prev) => ({ ...prev, [entryId]: { ...prev[entryId], progress } }));
            }
          };
          xhr.onload = () => {
            if (!isCurrent()) { resolve(); return; }
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const responseJson = JSON.parse(xhr.responseText);
                const subtitleId = responseJson.subtitleId.toString();

                setSubtitleEntries((prev) => ({ ...prev, [entryId]: { ...prev[entryId], id: subtitleId, stage: "uploaded", progress: 100 } }));
                resolve();
              } catch (err) {
                reject(err);
              }
            } else {
              reject(handleHttpError(xhr));
            }
          };
          xhr.onerror = () => isCurrent() ? reject(new Error("Network error")) : resolve();
          xhr.onabort = () => resolve();
          xhr.onloadend = () => {
            if (subtitleXhrs.current.get(entryId) === xhr) subtitleXhrs.current.delete(entryId);
          };
          xhr.send(formData);
        });
      } catch (err) {
        if (!mounted.current || !currentSubtitles.current.includes(file)) return;
        setSubtitleEntries((prev) => ({ ...prev, [entryId]: { ...prev[entryId], stage: "error" } }));
        throw err;
      }
    },
    [
      assetId,
      handleHttpError,
    ]
  );

  const cancelUpload = (field: keyof FileFields) => {
    if (field === "subtitles") {
      currentSubtitles.current = [];
      subtitleXhrs.current.forEach((xhr) => xhr.abort());
      subtitleXhrs.current.clear();
      setSubtitleEntries({});
      onFileChange("subtitles")([]);
    } else {
      if (field === "mainVideo" || field === "trailer") {
        abortControllersRef.current[field]?.abort();
        abortControllersRef.current[field] = undefined;
        resetVideoMeta(field);
      }
      const xhr = xhrRefs.current[field];
      if (xhr && !Array.isArray(xhr)) {
        xhr.abort();
      }
      xhrRefs.current[field] = undefined;
      setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
      setProgresses((prev) => ({ ...prev, [field]: 0 }));
      onFileChange(field)(null);
    }
  };

  const deleteFile = async (field: keyof FileFields) => {
    const token = getCookie("userToken");

    if (!token) {
      alert("Please log in to delete files");
      return;
    }

    let assetId: string | undefined;
    let endpoint: string = "";

    switch (field) {
      case "mainVideo":
      case "trailer":
        assetId = assetIds[field];
        if (!assetId) {
          alert(`No ${field} ID found to delete`);
          return;
        }
        endpoint = `/deletevideo/${encodeURIComponent(assetId)}`;
        break;
      case "heroImage":
      case "thumbnail":
        assetId = imageIds[field];
        if (!assetId) {
          alert(`No ${field} ID found to delete`);
          return;
        }
        endpoint = `/deleteimage/${encodeURIComponent(assetId)}`;
        break;
      case "subtitles":
        // For subtitles, use deleteSubtitle for individual deletion
        alert("Use deleteSubtitle for individual subtitle deletion");
        return;
      default:
        console.warn(`Unsupported delete field: ${field}`);
        return;
    }

    try {
      const res = await fetch(`${getApiOrigin()}${endpoint}`, {
        method: "DELETE",
        headers: {
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Delete failed: ${res.status} - ${errorText}`);
      }

      alert(`${field} deleted successfully`);

      onFileChange(field)(null);
      setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
      setProgresses((prev) => ({ ...prev, [field]: 0 }));
      if (field === "mainVideo" || field === "trailer") {
        resetVideoMeta(field);
      }

      if (field === "heroImage" || field === "thumbnail") {
        setImageIds((prev) => ({ ...prev, [field]: undefined }));
      } else {
        setAssetIds((prev) => ({ ...prev, [field]: undefined }));
      }
    } catch (err: any) {
      console.error(`Error deleting ${field}:`, err);
      alert(err.message || `An error occurred while deleting ${field}`);
    }
  };

  const deleteSubtitle = async (index: number) => {
    const file = currentSubtitles.current[index];
    if (!file) return;
    const entryId = subtitleEntryId(file);
    const token = getCookie("userToken");

    if (!token) {
      alert("Please log in to delete subtitles");
      return;
    }

    const subtitleId = getSubtitleId(index);

    if (!subtitleId) {
      alert("No subtitle ID found to delete at index " + index);
      return;
    }

    const endpoint = `/api/Asset/delete-subtitle/${encodeURIComponent(
      subtitleId
    )}`;

    try {
      const res = await fetch(`${getApiOrigin()}${endpoint}`, {
        method: "DELETE",
        headers: {
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Delete failed: ${res.status} - ${errorText}`);
      }

      alert(`Subtitle #${index + 1} deleted successfully`);

      // Update state to remove the deleted subtitle
      const newSubtitles = currentSubtitles.current.filter((entry) => subtitleEntryId(entry) !== entryId);
      currentSubtitles.current = newSubtitles;
      onFileChange("subtitles")(newSubtitles);
      setSubtitleEntries((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    } catch (err: any) {
      console.error(`Error deleting subtitle #${index + 1}:`, err);
      alert(
        err.message || `An error occurred while deleting subtitle #${index + 1}`
      );
    }
  };

  useEffect(() => {
    files.subtitles.forEach((file, index) => {
      if (!(file as any).isPlaceholder && uploadStages.subtitles[index] === "idle") {
        uploadSubtitle(file).catch(console.error);
      }
    });

    // Handle other files as before
    (["mainVideo", "trailer", "heroImage", "thumbnail"] as const).forEach(
      (field) => {
        const file = files[field];
        if (file !== previousFiles.current[field]) {
          previousFiles.current[field] = file;
          if (file && !(file as any).isPlaceholder) {
            setUploadStages((prev) => ({ ...prev, [field]: "idle" }));
            setProgresses((prev) => ({ ...prev, [field]: 0 }));
            if (uploadStages[field] !== "idle") return;
          }
        }
        if (file && !(file as any).isPlaceholder && uploadStages[field] === "idle") {
          uploadFile(field, file).catch(console.error);
        }
      }
    );
  }, [files, uploadStages, uploadFile, uploadSubtitle]);

  // Set uploadStage to 'uploaded' for placeholder files so the UI shows them as filled
  useEffect(() => {
    // For main file fields
    (["mainVideo", "trailer", "heroImage", "thumbnail"] as const).forEach(
      (field) => {
        const file = files[field];
        if (
          file &&
          (file as any).isPlaceholder &&
          uploadStages[field] !== "uploaded"
        ) {
          setUploadStages((prev) => ({ ...prev, [field]: "uploaded" }));
        }
      }
    );
    // eslint-disable-next-line
  }, [files]);

  useEffect(() => {
    setAssetIds((prev) => {
      const updated: typeof prev = { ...prev };
      let changed = false;
      if (mainVideoId && prev.mainVideo !== mainVideoId) {
        updated.mainVideo = mainVideoId;
        changed = true;
      }
      if (trailerId && prev.trailer !== trailerId) {
        updated.trailer = trailerId;
        changed = true;
      }
      return changed ? updated : prev;
    });
    setImageIds((prev) => {
      const updated: typeof prev = { ...prev };
      let changed = false;
      if (heroImageId && prev.heroImage !== heroImageId) {
        updated.heroImage = heroImageId;
        changed = true;
      }
      if (thumbnailId && prev.thumbnail !== thumbnailId) {
        updated.thumbnail = thumbnailId;
        changed = true;
      }
      return changed ? updated : prev;
    });
    // Only run when these change
    // eslint-disable-next-line
  }, [mainVideoId, trailerId, heroImageId, thumbnailId]);

  const handleSubtitleLanguageChange = async (
    index: number,
    language: string
  ) => {
    const file = currentSubtitles.current[index];
    if (!file) return;
    const entryId = subtitleEntryId(file);
    setSubtitleEntries((prev) => ({ ...prev, [entryId]: { ...(prev[entryId] || { stage: "uploaded", progress: 100 }), language } }));

    // Get the subtitle ID for this index
    const subtitleId = getSubtitleId(index);

    if (!subtitleId) {
      alert("Subtitle ID not found for this subtitle.");
      return;
    }

    const token = getCookie("userToken");
    if (!token) {
      alert("Please log in to change subtitle language");
      window.location.href = "/login";
      return;
    }

    try {
      await assetApi.postApiAssetChangeSubtitleLanguageSubtitleId(
        Number.parseInt(subtitleId, 10),
        { newLanguage: language } as any
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to change subtitle language";
      alert(String(msg));
    }
  };

  return (
    <div className="upload-column">
      <MainVideoUpload
        file={files.mainVideo}
        onChange={onFileChange("mainVideo")}
        uploadStage={uploadStages.mainVideo}
        progress={progresses.mainVideo}
        onCancel={() => cancelUpload("mainVideo")}
        onDelete={() => deleteFile("mainVideo")}
        isInvalid={isMainVideoInvalid}
        isEditingPublished={isEditingPublished}
      >
        <VrMediaVerification
          title="Main video"
          probing={!!probing.mainVideo}
          result={revalidateProbe(probeResults.mainVideo, vrFormat, "mainVideo")}
          format={vrFormat}
          serverProbe={serverProbe.mainVideo}
        />
        <TranscodeStatusView
          status={mainTranscode.status}
          unavailable={mainTranscode.unavailable}
        />
      </MainVideoUpload>
      <TrailerUpload
        file={files.trailer}
        onChange={onFileChange("trailer")}
        uploadStage={uploadStages.trailer}
        progress={progresses.trailer}
        onCancel={() => cancelUpload("trailer")}
        onDelete={() => deleteFile("trailer")}
        isEditingPublished={isEditingPublished}
      >
        <VrMediaVerification
          title="Trailer"
          probing={!!probing.trailer}
          result={revalidateProbe(probeResults.trailer, vrFormat, "trailer")}
          format={vrFormat}
          serverProbe={serverProbe.trailer}
        />
        <TranscodeStatusView
          status={trailerTranscode.status}
          unavailable={trailerTranscode.unavailable}
        />
      </TrailerUpload>
      <HeroImageUpload
        file={files.heroImage}
        onChange={onFileChange("heroImage")}
        uploadStage={uploadStages.heroImage}
        progress={progresses.heroImage}
        onCancel={() => cancelUpload("heroImage")}
        onDelete={() => deleteFile("heroImage")}
        isInvalid={isHeroImageInvalid}
        isEditingPublished={isEditingPublished}
      />
      <ThumbnailUpload
        file={files.thumbnail}
        onChange={onFileChange("thumbnail")}
        uploadStage={uploadStages.thumbnail}
        progress={progresses.thumbnail}
        onCancel={() => cancelUpload("thumbnail")}
        onDelete={() => deleteFile("thumbnail")}
        isEditingPublished={isEditingPublished}
      />
      <SubtitlesUpload
        files={files.subtitles}
        onChange={onFileChange("subtitles")}
        uploadStages={uploadStages.subtitles}
        progresses={files.subtitles.map((file) => subtitleEntries[subtitleEntryId(file)]?.progress || 0)}
        onCancel={() => cancelUpload("subtitles")}
        onDeleteSubtitle={deleteSubtitle}
        languages={files.subtitles.map((file) => subtitleEntries[subtitleEntryId(file)]?.language ?? (file as any).language ?? "")}
        onLanguageChange={handleSubtitleLanguageChange}
        isEditingPublished={isEditingPublished}
      />
    </div>
  );
};

export default UploadFile;
